import { RagChatReferenceItem } from '../../../domain/ragChat/types';

import { gaClickEvent } from '../../../utils/googleAnalytics';
import { parseNumber } from '../../../utils/parseNumber';
import { ExternalLink } from '../../atoms/ExternalLink/ExternalLink';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';

/**
 * 本文と脚注 ^[n] を分解し、脚注を <sup><Link/></sup> に置き換える
 *
 * sample:
 * ```
 * const content = "これはテストです^[1]。";
 * const { parseContent, markers } = parseFootnotes(content);
 *
 * parseContent = [
 *   "これはテストです",
 *  <sup key="1-0">
 *    <Link href="#ref-1" underline="none">
 *     1
 *    </Link>
 *  </sup>,
 *   "。"
 * ]
 * markers = [1]
 * ```
 *
 */
export const parseFootnotes = (
  content: string,
  reference_documents: RagChatReferenceItem[] | undefined,
  hoverHandlers: (marker: number) => {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  }
): {
  parseContent: React.ReactNode[];
  /**
   * 脚注番号の配列（ユニーク）
   */
  markers: number[];
} => {
  const markers: number[] = [];
  const parts: React.ReactNode[] = [];

  let lastIdx = 0;
  const regex = /\^\[(\d+)]/g;
  let m: RegExpExecArray | null;

  while ((m = regex.exec(content))) {
    const [match, numStr] = m;
    const num = parseNumber(numStr);

    // URLの生成
    const rawUrl = reference_documents?.find((ref) => ref.marker === num)?.url;

    let parsedUrl: string | undefined;
    try {
      parsedUrl = rawUrl ? new URL(rawUrl).toString() : undefined;
    } catch (e) {
      console.warn('Invalid URL:', rawUrl);
      parsedUrl = undefined;
    }

    // マーカー前のプレーンテキスト
    if (m.index > lastIdx) {
      parts.push(content.slice(lastIdx, m.index));
    }

    // <sup>リンク</sup>
    parts.push(
      <sup key={num + '-' + m.index}>
        <Tooltip
          title={`参照元のリンクを開く`}
          placement="top"
          arrow
          slotProps={{
            tooltip: { sx: { fontSize: '0.7rem' } },
          }}
        >
          <ExternalLink
            href={parsedUrl}
            underline="none" // 下線を非表示
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '18px', // ボタンの幅
              height: '16px', // ボタンの高さ
              borderRadius: '50%', // 丸くする
              backgroundColor: 'grey.500', // 背景色
              color: 'white', // テキストの色
              '&:hover': {
                backgroundColor: 'grey.600', // ホバー時の背景色
              },
              marginLeft: '3px',
            }}
            {...hoverHandlers(num)}
            onClick={() => {
              gaClickEvent('AIアシスタント参照元リンク');
            }}
          >
            {num}
          </ExternalLink>
        </Tooltip>
      </sup>
    );

    // markers に同じ num が存在しない場合のみ追加
    if (!markers.some((marker) => marker === num)) {
      markers.push(num);
    }
    lastIdx = m.index + match.length;
  }

  // 末尾のプレーンテキスト
  if (lastIdx < content.length) parts.push(content.slice(lastIdx));

  // markers を番号順にソート
  markers.sort((a, b) => a - b);

  return { parseContent: parts, markers };
};
